import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { SmartUserSelector } from '@/components/form/fields/SmartUserSelector';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { Form } from '@/components/form/Form';
import { useModal } from '@/components/modal/useModal';
import InfoTooltip from '@/components/tooltip/InfoTooltip';
import UnsavedChanges from '@/components/unsavedChanges/UnsavedChanges';
import { useDirtyFormGuard } from '@/hooks/useDirtyFormGuard';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { ServiceRequestLayout } from '@/layouts/ServiceRequestLayout';
import { HaloEquipmentDto } from '@/services/api/contracts/companies';
import { useCategories, useGetRateCards } from '@/services/api/hooks/catalog';
import { useCompany, useGetCompanyHaloEquipment, useGetCompanyHaloStock } from '@/services/api/hooks/companies';
import { RoleType, useUser } from '@/services/authentication/hooks/user';
import { resolveDeployOverrideRateCardId, resolveDeployProcessingRateCardId, resolveReturnOverrideRateCardId, resolveReturnProcessingRateCardId } from '@/utils/categoryOverrides';
import { formatCurrency } from '@/utils/currency';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaHeadset } from 'react-icons/fa';
import { FiCheck, FiGlobe, FiSettings, FiShield } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { useSandbox } from '../../contexts/SandboxContext';
import { AdvDeviceReplacementModal } from './AdvDeviceReplacementModal';

const REQUIRE_SERIALIZED_ONLY = true;

const AdvDeviceReplacementSchema = z.object({
  fullName: z.string().min(1, 'Required'),
  workEmail: z.string().email('Invalid email').optional().or(z.literal('na')).or(z.literal('')),
  personalEmail: z.string().optional(),
  mobileNumber: z.string().optional(),
  noCorporateEmail: z.boolean().default(false),
  selectedDeviceId: z.string().optional(),
  manualDeviceType: z.string().optional(),
  faultyMakeModel: z.string().optional(),
  faultySerialNumber: z.string().optional(),
  faultyAssetTag: z.string().optional(),
  replacementReason: z.string().min(1, 'Reason for replacement is required'),
  replacementSelection: z.string().optional(),
  replacementOverrideText: z.string().optional(),
  selectedLoanerId: z.string().optional(),
  selectedReturnProcessingId: z.string().optional()
}).catchall(z.any()).superRefine((data, ctx) => {
  if (!data.noCorporateEmail && (!data.workEmail || data.workEmail.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required unless checked",
      path: ["workEmail"],
    });
  }

  if (!data.fullName || data.isManualEntryOverride) {
    if (!data.manualDeviceType) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['manualDeviceType'] });
    } else {
      if (!data.faultyMakeModel || data.faultyMakeModel.trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['faultyMakeModel'] });
      }
      if (!data.faultySerialNumber || data.faultySerialNumber.trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['faultySerialNumber'] });
      }
    }
  } else {
    if (!data.selectedDeviceId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'You must select a device to replace', path: ['selectedDeviceId'] });
    } else {
      if (!data.faultyMakeModel || data.faultyMakeModel.trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['faultyMakeModel'] });
      }
      if (!data.faultySerialNumber || data.faultySerialNumber.trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['faultySerialNumber'] });
      }
    }
  }

  if (data.selectedDeviceId || data.manualDeviceType) {
    if (!data.replacementSelection) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'You must select a replacement device', path: ['replacementSelection'] });
    } else if (data.replacementSelection === 'OVERRIDE' && !data.replacementOverrideText) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Override reason/details required', path: ['replacementOverrideText'] });
    }
  }
});

type FormFields = z.infer<typeof AdvDeviceReplacementSchema>;

const getAdminTooltipText = (companyName: string) => (
  <div className="flex flex-col gap-3 text-left text-sm w-[540px]">
    <div className="p-4 rounded-md border border-gray-200 bg-gray-50">
      <h6 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
        <FaHeadset className="w-4 h-4 text-[#41D2F1]" />
        Ticket Labour
      </h6>
      <ul className="space-y-4">
        {/* Outbound Processes */}
        <li>
          <div className="font-semibold text-gray-800 mb-2">Outbound Processes</div>
          <ul className="space-y-2 ml-2">
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <span className="text-gray-700">Create user</span>
            </li>
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <span className="text-gray-700">Assign products to user</span>
            </li>
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <div className="text-gray-700 w-full">
                <span className="block mb-2">Comms - Inform user:</span>
                <ul className="space-y-1.5 ml-1 border-l-2 border-emerald-500 pl-4">
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    Of the shipment from {companyName}
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    Products they will receive
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    Tracking details
                  </li>
                </ul>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <span className="text-gray-700">Track until delivered</span>
            </li>
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <span className="text-gray-700">Confirm with user phyiscal receipt of goods</span>
            </li>
          </ul>
        </li>

        {/* Inbound Processes */}
        <li>
          <div className="font-semibold text-gray-800 mb-2">Inbound Processes</div>
          <ul className="space-y-2 ml-2">
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <div className="text-gray-700 w-full">
                <span className="block mb-2">Instructions:</span>
                <ul className="space-y-1.5 ml-1 border-l-2 border-emerald-500 pl-4">
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    How to package the equipment
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    How to book home/office collection at their convenience
                  </li>
                </ul>
              </div>
            </li>
            {[
              "Chaser 1 - Email",
              "Chaser 2 - Email",
              "Chaser 3 - Phone call",
              "Receive goods on system",
              "Check serials match",
              "Check and apply customer lifecycle policies",
              "Assign to correct processing path",
              "Unassign products from user"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </li>
      </ul>
    </div>
  </div>
);

const logisticsTooltipText = (
  <div className="flex flex-col gap-3 text-left text-sm w-[540px]">
    <div className="p-3 rounded-md border border-gray-200 bg-gray-50 flex items-start gap-3">
      <FiGlobe className="w-5 h-5 text-[#41D2F1] mt-0.5 shrink-0" />
      <p className="text-gray-700 leading-relaxed">
        For US and EU regions, there is one average logistics cost to cover the whole region. This gives predictable costs and reduces admin.
      </p>
    </div>
    <div className="p-3 rounded-md border border-gray-200 bg-gray-50 flex items-start gap-3">
      <FiShield className="w-5 h-5 text-[#EB6626] mt-0.5 shrink-0" />
      <p className="text-gray-700 leading-relaxed">
        All goods are insured for their full amount whilst in transit and in our warehousing locations.
      </p>
    </div>
  </div>
);

const processingTooltipText = (
  <div className="flex flex-col gap-3 text-left text-sm w-[540px]">
    <div className="p-4 rounded-md border border-gray-200 bg-gray-50">
      <h6 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
        <FiSettings className="w-4 h-4 text-[#EB6626]" />
        Goods Receiving Fee
      </h6>
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
          <div className="text-gray-700 w-full">
            <span className="block mb-2">Goods receiving process:</span>
            <ul className="space-y-1.5 ml-1 border-l-2 border-emerald-500 pl-4">
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Scan goods in
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Cross check serials to match
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Allocate unique stocking ID
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Shelve
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
  </div>
);

export const AdvDeviceReplacementPage: FC = () => {
  const { partnerId, companyId } = useParams();
  const navigate = useNavigate();
  if (!(partnerId && companyId)) throw new Error('Missing params');

  const [company] = useCompany(partnerId, companyId);
  const [equipmentPayload] = useGetCompanyHaloEquipment(partnerId, companyId);
  const [haloStock] = useGetCompanyHaloStock(partnerId, companyId);
  const [rateCards] = useGetRateCards(undefined, partnerId, companyId);
  const [categories] = useCategories();
  
  const { isSandboxMode, setTelemetry } = useSandbox();
  const user = useUser();
  const isAdmin = user?.roles?.some((r: any) => r.type === RoleType.Admin);

  const availableDeploymentCards = useMemo(() => {
    return rateCards?.items?.filter(rc => rc.propertyBag?.applicableRequestTypes?.includes('AdvDeviceReplacement')) || [];
  }, [rateCards]);

  const availableCollectionCards = useMemo(() => {
    return rateCards?.items?.filter(rc => rc.propertyBag?.applicableRequestTypes?.includes('OffboardUser')) || [];
  }, [rateCards]);

  const loanerCards = useMemo(() => {
    return rateCards?.items?.filter(rc => rc.propertyBag?.addonCategory === 'Loaner') || [];
  }, [rateCards]);

  const returnProcessingCards = useMemo(() => {
    return rateCards?.items?.filter(rc => rc.propertyBag?.addonCategory === 'ReturnProcessing') || [];
  }, [rateCards]);

  const uniqueUsers = useMemo(() => {
    if (!equipmentPayload) return [];
    const rawUsers = Array.from(new Set(equipmentPayload.map((e: HaloEquipmentDto) => e.user))).filter(Boolean) as string[];
    
    return rawUsers.map(raw => {
      const parts = raw.split('|').map(p => p.trim());
      return {
        name: parts[0] || raw,
        email: parts.length > 1 && parts[1].includes('@') ? parts[1] : null,
        rawValue: raw
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [equipmentPayload]);

  const methods = useForm<FormFields>({
    mode: 'onChange',
    resolver: zodResolver(AdvDeviceReplacementSchema),
    defaultValues: {
      fullName: '',
      workEmail: '',
      personalEmail: '',
      mobileNumber: '',
      noCorporateEmail: false,
      selectedDeviceId: '',
      manualDeviceType: '',
      faultyMakeModel: '',
      faultySerialNumber: '',
      faultyAssetTag: '',
      replacementReason: '',
      replacementSelection: '',
      replacementOverrideText: '',
      selectedLoanerId: '',
      selectedReturnProcessingId: '',
      isManualEntryOverride: false
    }
  });

  const [selectedUserEquipment, setSelectedUserEquipment] = useState<HaloEquipmentDto[]>([]);
  const [checkoutPayload, setCheckoutPayload] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const modalProps = useModal();

  useEffect(() => {
    methods.setValue('isManualEntryOverride', isManualEntry);
  }, [isManualEntry, methods]);

  const handleNextStep = async () => {
    let fieldsToValidate: any[] = [];
    if (currentStep === 1) {
      const formValues = methods.getValues();
      const forceManual = !formValues.fullName || isManualEntry;
      
      const baseFields = ['fullName', 'workEmail', 'personalEmail', 'mobileNumber', 'replacementReason'];
      
      if (forceManual) {
        fieldsToValidate = [...baseFields, 'manualDeviceType', 'faultyMakeModel', 'faultySerialNumber'];
      } else {
        fieldsToValidate = [...baseFields, 'selectedDeviceId'];
      }
    } else if (currentStep === 2) {
      fieldsToValidate = ['replacementSelection', 'replacementOverrideText'];
    } else if (currentStep === 3) {
      fieldsToValidate = ['selectedLoanerId', 'selectedReturnProcessingId'];
    }
    
    const isValid = await methods.trigger(fieldsToValidate);
    if (isValid) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  const handleMacroSelect = (selectedUser: any | null) => {

    if (!selectedUser) {
      setSelectedUserEquipment([]);
      methods.setValue('selectedDeviceId', '');
      methods.setValue('faultyMakeModel', '', { shouldValidate: true, shouldDirty: true });
      methods.setValue('faultySerialNumber', '', { shouldValidate: true, shouldDirty: true });
      methods.setValue('faultyAssetTag', '', { shouldValidate: true, shouldDirty: true });
      return;
    }

    if (equipmentPayload) {
      const userEquipment = equipmentPayload.filter((e: HaloEquipmentDto) => {
        if (e.user !== selectedUser.rawValue) return false;
        const normalizedType = (e.type || '').toLowerCase().replace(/s$/, '');
        const category = categories?.find(c => c.name.toLowerCase().replace(/s$/, '') === normalizedType);
        return REQUIRE_SERIALIZED_ONLY ? category?.isSerialized === true : true;
      });
      setSelectedUserEquipment(userEquipment);
      methods.setValue('manualDeviceType', '');
    }
  };

  const formValues = methods.watch();

  const isResettingRef = useRef(false);
  const prevSelectedDeviceRef = useRef<string | null>('');

  useEffect(() => {
    if (isResettingRef.current) {
      prevSelectedDeviceRef.current = formValues.selectedDeviceId || null;
      return;
    }
    if (formValues.selectedDeviceId !== prevSelectedDeviceRef.current) {
      // Clean up previous
      if (prevSelectedDeviceRef.current) {
        methods.setValue('faultyMakeModel', '', { shouldValidate: true, shouldDirty: true });
        methods.setValue('faultySerialNumber', '', { shouldValidate: true, shouldDirty: true });
        methods.setValue('faultyAssetTag', '', { shouldValidate: true, shouldDirty: true });
      }

      // Set new
      if (formValues.selectedDeviceId) {
        const selectedDevice = selectedUserEquipment.find(e => (e.serial || e.sku) === formValues.selectedDeviceId);
        if (selectedDevice) {
          methods.setValue('faultyMakeModel', selectedDevice.sku || '', { shouldValidate: true, shouldDirty: true });
          methods.setValue('faultySerialNumber', selectedDevice.serial || '', { shouldValidate: true, shouldDirty: true });
          methods.setValue('faultyAssetTag', '', { shouldValidate: true, shouldDirty: true });
        }
      }

      methods.setValue('replacementSelection', '', { shouldValidate: true, shouldDirty: true });
      methods.setValue('replacementOverrideText', '', { shouldValidate: true, shouldDirty: true });
      prevSelectedDeviceRef.current = formValues.selectedDeviceId || null;
    }
  }, [formValues.selectedDeviceId, selectedUserEquipment, methods]);

  const prevManualDeviceTypeRef = useRef<string | null>('');
  useEffect(() => {
    if (isResettingRef.current) {
      prevManualDeviceTypeRef.current = formValues.manualDeviceType || null;
      return;
    }
    if (formValues.manualDeviceType !== prevManualDeviceTypeRef.current) {
      if (prevManualDeviceTypeRef.current) {
        methods.setValue('faultyMakeModel', '', { shouldValidate: true, shouldDirty: true });
        methods.setValue('faultySerialNumber', '', { shouldValidate: true, shouldDirty: true });
        methods.setValue('faultyAssetTag', '', { shouldValidate: true, shouldDirty: true });
      }
      methods.setValue('replacementSelection', '', { shouldValidate: true, shouldDirty: true });
      methods.setValue('replacementOverrideText', '', { shouldValidate: true, shouldDirty: true });
      prevManualDeviceTypeRef.current = formValues.manualDeviceType || null;
    }
  }, [formValues.manualDeviceType, methods]);

  const prevReplacementSelectionRef = useRef<string | null>('');
  useEffect(() => {
    if (isResettingRef.current) {
      prevReplacementSelectionRef.current = formValues.replacementSelection || null;
      return;
    }
    if (formValues.replacementSelection !== prevReplacementSelectionRef.current) {
      if (formValues.replacementSelection !== 'OVERRIDE') {
        methods.setValue('selectedLoanerId', '', { shouldValidate: true, shouldDirty: true });
      }
      prevReplacementSelectionRef.current = formValues.replacementSelection || null;
    }
  }, [formValues.replacementSelection, methods]);

  const aggregatedHaloStock = useMemo(() => {
    if (!haloStock) return [];
    const acc: Record<string, any> = {};
    for (const item of haloStock) {
      if (!item.sku) continue;
      if (!acc[item.sku]) {
        acc[item.sku] = { ...item, stockLocations: new Set(item.stockLocation ? [item.stockLocation] : []), stockBreakdown: [{ site: item.stockLocation || item.siteID || 'Unknown', qty: item.stockQuantity }] };
      } else {
        acc[item.sku].stockQuantity += item.stockQuantity;
        if (item.stockLocation) {
          acc[item.sku].stockLocations.add(item.stockLocation);
        }
        const siteName = item.stockLocation || item.siteID || 'Unknown';
        const existingSite = acc[item.sku].stockBreakdown.find((s: any) => s.site === siteName);
        if (existingSite) {
          existingSite.qty += item.stockQuantity;
        } else {
          acc[item.sku].stockBreakdown.push({ site: siteName, qty: item.stockQuantity });
        }
      }
    }
    return Object.values(acc).map((item: any) => ({
      ...item,
      stockLocation: Array.from(item.stockLocations).join(' | ')
    }));
  }, [haloStock]);

  const availableReplacements = useMemo(() => {
    let targetCategoryName = '';
    if (formValues.selectedDeviceId) {
      const selectedDevice = selectedUserEquipment.find(e => (e.serial || e.sku) === formValues.selectedDeviceId);
      if (selectedDevice) {
        const normalizedType = (selectedDevice.type || '').toLowerCase().replace(/s$/, '');
        const category = categories?.find(c => c.name.toLowerCase().replace(/s$/, '') === normalizedType);
        if (category) targetCategoryName = category.name;
      }
    } else if (formValues.manualDeviceType) {
      targetCategoryName = formValues.manualDeviceType;
    }

    if (!targetCategoryName || !aggregatedHaloStock) return [];

    return aggregatedHaloStock.filter((s: any) => s.stockQuantity > 0 && s.itemType?.toLowerCase() === targetCategoryName.toLowerCase());
  }, [formValues.selectedDeviceId, formValues.manualDeviceType, selectedUserEquipment, categories, aggregatedHaloStock]);

  const { inferredServices, runningTotal, telemetryData } = useMemo(() => {
    let total = 0;
    const services: Array<any> = [];
    const telemetry = {
      collection: { totalDeadWeight: 0, totalVolumetricWeight: 0, chargeableWeight: 0, overriddenSkus: [] as string[], itemLog: [] as any[] },
      deployment: { totalDeadWeight: 0, totalVolumetricWeight: 0, chargeableWeight: 0, overriddenSkus: [] as string[], itemLog: [] as any[] }
    };

    const selectedDeviceId = formValues.selectedDeviceId;
    let hasPack = false;
    let selectedDevice: any = null;

    if (selectedDeviceId) {
      selectedDevice = selectedUserEquipment.find(e => (e.serial || e.sku) === selectedDeviceId);
      hasPack = selectedDevice?.hasLifecyclePack || false;
    } else {
      const action = formValues.fullName;
      if (!action || isManualEntry) {
        if (formValues.manualDeviceType) {
          selectedDevice = {
            type: formValues.manualDeviceType,
            isManualEntry: true
          };
        }
      }
    }

    if (!selectedDevice) return { inferredServices: services, runningTotal: total, telemetryData: telemetry };

    const category = categories?.find(c => {
      let cName = c.name.toLowerCase().trim();
      if (cName.endsWith('s')) cName = cName.slice(0, -1);
      let nt = (selectedDevice.type || '').toLowerCase().trim();
      if (nt.endsWith('s')) nt = nt.slice(0, -1);
      return cName === nt;
    });

    // Pass 1: Base Pack
    const basePack = availableDeploymentCards.find(c => c.propertyBag.basePackForRequestType === 'AdvDeviceReplacement');
    
    if (basePack) {
      const price = (basePack.price?.price || 0) / 100;
      services.push({ listSection: 'admin', rateCard: basePack, quantity: 1, name: basePack.name || 'ADR Base Pack', labels: ['Base Pack'], hasLifecyclePack: hasPack, price, total: price });
      total += price;
    }

    // Pass 2a: Collection Handling
    let colHandlingCard = undefined;
    const colProcessingId = resolveReturnProcessingRateCardId(category, company?.region?.id);
    const colOverrideId = resolveReturnOverrideRateCardId(category, company?.region?.id);
    const suppressSerial = !!colOverrideId;
    
    if (colProcessingId) {
      colHandlingCard = rateCards?.items?.find(r => r.id === colProcessingId);
    }
    if (!colHandlingCard) {
      colHandlingCard = availableCollectionCards.find(c => c.propertyBag.itemRateForRequestType === 'OffboardUser');
    }

    let injectedCol = false;
    if (colHandlingCard) {
      const price = (colHandlingCard.price?.price || 0) / 100;
      services.push({
        listSection: 'processing',
        rateCard: colHandlingCard,
        quantity: 1,
        name: colHandlingCard.name || 'Collection Handling',
        labels: ['Collection Processing'],
        hasLifecyclePack: hasPack,
        associatedPhysicalItems: [{ type: selectedDevice.type, hasLifecyclePack: hasPack, isCollectionItem: true, isManualEntry: selectedDevice.isManualEntry, suppressSerialInput: suppressSerial }],
        isInventoryItem: false,
        price,
        total: price
      });
      total += price;
      injectedCol = true;
    }

    if (!injectedCol && basePack) {
       const bpService = services.find(s => s.rateCard?.id === basePack.id);
       if (bpService) {
         bpService.associatedPhysicalItems = bpService.associatedPhysicalItems || [];
         bpService.associatedPhysicalItems.push({ type: selectedDevice.type, hasLifecyclePack: hasPack, isCollectionItem: true, isManualEntry: selectedDevice.isManualEntry, suppressSerialInput: suppressSerial });
       }
    }

    // Pass 2b: Deployment Handling
    let depHandlingCard = undefined;
    const depProcessingId = resolveDeployProcessingRateCardId(category, company?.region?.id);
    if (depProcessingId) {
      depHandlingCard = rateCards?.items?.find(r => r.id === depProcessingId);
    }
    if (!depHandlingCard) {
      depHandlingCard = availableDeploymentCards.find(c => c.propertyBag.itemRateForRequestType === 'AdvDeviceReplacement') || rateCards?.items?.find(c => c.propertyBag.itemRateForRequestType === 'DeployInventory');
    }

    let deployedItemName = selectedDevice.type;
    if (formValues.replacementSelection === 'OVERRIDE') {
      deployedItemName = `Override: ${formValues.replacementOverrideText}`;
    } else if (formValues.replacementSelection) {
      const stockItem = aggregatedHaloStock?.find((s: any) => s.sku === formValues.replacementSelection);
      if (stockItem) {
        deployedItemName = stockItem.item || stockItem.sku;
      }
    }

    let injectedDep = false;
    if (depHandlingCard) {
      const price = (depHandlingCard.price?.price || 0) / 100;
      services.push({
        listSection: 'processing',
        rateCard: depHandlingCard,
        quantity: 1,
        name: depHandlingCard.name || 'Deployment Handling',
        labels: ['Deployment Processing'],
        hasLifecyclePack: false,
        associatedPhysicalItems: [{ type: deployedItemName, hasLifecyclePack: false, isInventoryItem: true }],
        isInventoryItem: false,
        price,
        total: price
      });
      total += price;
      injectedDep = true;
    }

    if (!injectedDep) {
      services.push({
        listSection: 'processing',
        name: deployedItemName,
        quantity: 1,
        isInventoryItem: true,
        hasLifecyclePack: false
      });
    }

    // Pass 3: Collection Mirror (Telemetry Only)
    const colIsOverridden = !!colOverrideId;
    
    if (colIsOverridden) {
      telemetry.collection.overriddenSkus.push(selectedDevice.type);
    }

    telemetry.collection.itemLog.push({
      name: selectedDevice.type,
      quantity: 1,
      deadWeightKg: 0,
      baseVolumetricKg: 0,
      packingEfficiency: 100,
      packedVolumetricKg: 0,
      isOverridden: colIsOverridden
    });
    
    telemetry.collection.totalDeadWeight = 0;
    telemetry.collection.totalVolumetricWeight = 0;
    telemetry.collection.chargeableWeight = 0;

    // Pass 3: Deployment Mirror (Volumetric Shipping)
    let depDeadWt = 0;
    let depBaseVol = 0;
    let depPackEff = 100;
    let depPackedVol = 0;
    let depIsOverridden = false;

    if (category) {
      const depOverrideId = resolveDeployOverrideRateCardId(category, company?.region?.id);
      if (depOverrideId) {
        depIsOverridden = true;
        const overrideCard = availableDeploymentCards.find(r => r.id === depOverrideId);
        if (overrideCard) {
          telemetry.deployment.overriddenSkus.push(selectedDevice.type);
          const price = (overrideCard.price?.price || 0) / 100;
          services.push({ listSection: 'logistics', rateCard: overrideCard, name: overrideCard.name, quantity: 1, labels: ['Deployment Crate Fee'], price, total: price, associatedPhysicalItems: [{ type: deployedItemName, hasLifecyclePack: false, isInventoryItem: true, suppressSerialInput: true }] });
          total += price;
        }
      } else {
        depDeadWt = category.logisticsConfig?.deploy?.averageWeightKg || 0;
        depBaseVol = category.logisticsConfig?.deploy?.dimensionalWeightKg || 0;
        depPackEff = category.logisticsConfig?.deploy?.packingEfficiencyPercent || 100;
        depPackedVol = depBaseVol / (depPackEff / 100);
      }
    }
    telemetry.deployment.itemLog.push({
      name: selectedDevice.type,
      quantity: 1,
      deadWeightKg: depDeadWt,
      baseVolumetricKg: depBaseVol,
      packingEfficiency: depPackEff,
      packedVolumetricKg: depPackedVol,
      isOverridden: depIsOverridden
    });
    const depChargeableWeight = Math.max(depDeadWt, depPackedVol);
    telemetry.deployment.totalDeadWeight = depDeadWt;
    telemetry.deployment.totalVolumetricWeight = depPackedVol;
    telemetry.deployment.chargeableWeight = depChargeableWeight;

    if (depChargeableWeight > 0) {
      const tierCards = availableDeploymentCards.filter(c => c.propertyBag.maxChargeableWeightKg !== undefined && !c.propertyBag.isExclusiveOverride)
        .sort((a, b) => (a.propertyBag.maxChargeableWeightKg || 0) - (b.propertyBag.maxChargeableWeightKg || 0));
      if (tierCards.length > 0) {
        const matchedTier = tierCards.find(c => depChargeableWeight <= c.propertyBag.maxChargeableWeightKg!) || tierCards[tierCards.length - 1];
        const price = (matchedTier.price?.price || 0) / 100;
        services.push({ listSection: 'logistics', rateCard: matchedTier, name: matchedTier.name, quantity: 1, labels: ['ADR Shipping Tier'], price, total: price });
        total += price;
      }
    }

    // God-Mode Warranty Interceptor
    if (selectedDevice?.hasAdrWarranty) {
      services.forEach(s => {
        if (s.price && s.price > 0) {
          s.originalPrice = s.price;
          s.price = 0;
          s.total = 0;
        }
      });
      total = 0; // Recalculate to 0 since all prior core items are zeroed out
    }

    // Pass 4: Add-Ons
    const { selectedLoanerId, selectedReturnProcessingId } = formValues;
    if (selectedLoanerId) {
      const loaner = loanerCards.find(c => c.id === selectedLoanerId);
      if (loaner) {
        let price = (loaner.price?.price || 0) / 100;
        let originalPrice: number | undefined = undefined;

        if (loaner.propertyBag?.isCoveredByWarranty && selectedDevice?.hasAdrWarranty) {
          originalPrice = price;
          price = 0;
        }
        services.push({ listSection: 'addons', rateCard: loaner, name: loaner.name, quantity: 1, labels: ['Add-On'], price, total: price, originalPrice });
        total += price;
      }
    }

    if (selectedReturnProcessingId) {
      const returnAddon = returnProcessingCards.find(c => c.id === selectedReturnProcessingId);
      if (returnAddon) {
        let price = (returnAddon.price?.price || 0) / 100;
        let originalPrice: number | undefined = undefined;

        if (returnAddon.propertyBag?.isCoveredByWarranty && selectedDevice?.hasAdrWarranty) {
          originalPrice = price;
          price = 0;
        }
        services.push({ listSection: 'addons', rateCard: returnAddon, name: returnAddon.name, quantity: 1, labels: ['Add-On'], price, total: price, originalPrice });
        total += price;
      }
    }

    return { inferredServices: services, runningTotal: total, telemetryData: telemetry };
  }, [availableDeploymentCards, availableCollectionCards, loanerCards, returnProcessingCards, formValues.selectedDeviceId, formValues.manualDeviceType, formValues.manualMakeModel, formValues.manualSerialNumber, formValues.fullName, formValues.replacementSelection, formValues.replacementOverrideText, formValues.selectedLoanerId, formValues.selectedReturnProcessingId, selectedUserEquipment, categories, aggregatedHaloStock]);

  const telemetryString = JSON.stringify(telemetryData);
  useEffect(() => {
    if (isSandboxMode && telemetryString) {
      setTelemetry(JSON.parse(telemetryString));
    }
  }, [telemetryString, isSandboxMode, setTelemetry]);

  const onSubmit = async (data: any) => {
    if (returnProcessingCards.length > 0 && !data.selectedReturnProcessingId) {
      methods.setError('selectedReturnProcessingId', { type: 'manual', message: 'You must select a return processing option.' });
      return;
    }

    let selectedDevice = null;
    const action = data.fullName; // checking if a user was selected or entered

    const makeModel = data.faultyMakeModel;
    const serial = data.faultySerialNumber;
    const assetTag = data.faultyAssetTag;
    const tagStr = assetTag ? `, Tag: ${assetTag}` : '';

    if (!action || isManualEntry) {
      const type = data.manualDeviceType;

      selectedDevice = {
        type: type,
        makeModel: makeModel,
        serial: serial,
        assetTag: assetTag,
        isManualEntry: true
      };
    } else {
      selectedDevice = selectedUserEquipment.find((e: any) => (e.serial || e.sku) === data.selectedDeviceId);
    }

    const isOverride = data.replacementSelection === 'OVERRIDE';
    const replacementStockItem = aggregatedHaloStock?.find((s: any) => s.sku === data.replacementSelection);

    setCheckoutPayload({
      fullName: data.fullName,
      workEmail: data.workEmail,
      personalEmail: data.personalEmail,
      mobileNumber: data.mobileNumber,
      noCorporateEmail: data.noCorporateEmail,
      selectedDevice,
      replacementDevice: {
        isOverride,
        sku: isOverride ? undefined : data.replacementSelection,
        name: isOverride ? `Override: ${data.replacementOverrideText}` : (replacementStockItem?.item || data.replacementSelection),
        overrideText: data.replacementOverrideText
      },
      reason: data.replacementReason,
      inferredServices,
      runningTotal
    });

    modalProps.open();
  };

  const resetForm = useCallback(() => {
    isResettingRef.current = true;
    methods.reset({
      fullName: '',
      workEmail: '',
      personalEmail: '',
      mobileNumber: '',
      noCorporateEmail: false,
      selectedDeviceId: '',
      manualDeviceType: '',
      faultyMakeModel: '',
      faultySerialNumber: '',
      faultyAssetTag: '',
      replacementReason: '',
      replacementSelection: '',
      replacementOverrideText: '',
      selectedLoanerId: '',
      selectedReturnProcessingId: '',
      isManualEntryOverride: false
    });
    setSelectedUserEquipment([]);
    setCheckoutPayload(null);
    setCurrentStep(1);
    setIsManualEntry(false);
    
    setTimeout(() => {
      isResettingRef.current = false;
    }, 100);
  }, [methods]);

  const breadcrumbs: NavPage[] = [
    { name: 'Management', to: '' },
    { name: 'Adv. Device Replacement', to: '' }
  ];

  const isDirty = methods.formState.isDirty;
  useDirtyFormGuard(isDirty);

  return (
    <>
      <UnsavedChanges 
        show={isDirty} 
        title="Abandon Request"
        message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
        confirmText="Yes, Abandon"
        confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
        cancelText="No, Continue"
        cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
      />
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between items-start w-full">
          <div className="flex flex-col gap-2 items-start">
            <h3 className="text-3xl font-bold">Adv. Device Replacement</h3>
            <p className="font-medium">Request an emergency swap for a broken device</p>
          </div>
          {isAdmin && (
            isSandboxMode ? (
              <button
                type="button"
                onClick={() => navigate(`/msps/${partnerId}/companies/${companyId}/management/advDeviceReplacement`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors border border-red-200 shadow-sm"
              >
                <span>🚪</span> Exit Sandbox
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate(`/admin/sandbox/partners/${partnerId}/companies/${companyId}/adv-device-replacement`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono text-gray-500 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors border border-gray-200 shadow-sm"
              >
                <span>⚙️</span> Debug in Sandbox
              </button>
            )
          )}
        </div>
      </BreadcrumbPageHeader>
      
      <Form methods={methods} onSubmit={onSubmit}>
        <div className="px-4">
          <nav aria-label="Progress">
            <ol role="list" className="divide-y divide-gray-300 rounded-md bg-gray-100 overflow-hidden md:flex md:divide-y-0">
              {['Device', 'Replacement', 'Returns Processing'].map((stepName, stepIdx) => (
                <li key={stepName} className="relative md:flex md:flex-1">
                  <div className={`group flex w-full h-full items-center ${
                    stepIdx === 0 
                      ? 'md:[clip-path:polygon(0_0,calc(100%-1rem)_0,100%_50%,calc(100%-1rem)_100%,0_100%)]' 
                      : stepIdx === 2
                        ? 'md:[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,1rem_50%)] md:-ml-4'
                        : 'md:[clip-path:polygon(0_0,calc(100%-1rem)_0,100%_50%,calc(100%-1rem)_100%,0_100%,1rem_50%)] md:-ml-4'
                  } ${currentStep === stepIdx + 1 ? 'bg-primary-700' : currentStep > stepIdx + 1 ? 'bg-primary-50' : 'bg-white'}`}>
                    <span className={`flex items-center py-2.5 text-sm font-semibold ${stepIdx !== 0 ? 'px-4 md:pl-8' : 'px-4'}`}>
                      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2 ${currentStep > stepIdx + 1 ? 'border-primary-700 bg-primary-700' : currentStep === stepIdx + 1 ? 'border-white' : 'border-gray-300 bg-white'}`}>
                        {currentStep > stepIdx + 1 ? (
                          <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                            <path fillRule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <span className={`text-xs ${currentStep === stepIdx + 1 ? 'text-white' : 'text-gray-500'}`}>{stepIdx + 1}</span>
                        )}
                      </span>
                      <span className={`ml-4 text-sm font-semibold ${currentStep === stepIdx + 1 ? 'text-white' : currentStep > stepIdx + 1 ? 'text-gray-700' : 'text-gray-500'}`}>{stepName}</span>
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          </nav>
        </div>
        <ServiceRequestLayout
          title=""
          macroSlot={
            currentStep === 1 ? (
              <div className="bg-white p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="w-full">
                  <SmartUserSelector 
                    methods={methods as any} 
                    uniqueUsers={uniqueUsers} 
                    onSelect={handleMacroSelect} 
                    nameField="fullName" 
                    corpEmailField="workEmail" 
                    personalEmailLabel="Personal Email"
                    personalEmailOptional={true}
                    mobileNumberField="mobileNumber"
                    mobileNumberLabel="Personal Mobile Number"
                    mobileNumberOptional={true}
                    tooltipText="Select a user to pull their currently assigned equipment for replacement."
                  />
                </div>
              </div>
            ) : null
          }
          leftColumn={
            <div className="space-y-4 lg:space-y-6 pb-32">
              {currentStep === 1 && (
                <>
                  {(formValues.fullName && !isManualEntry) ? (
                    <div className="border border-gray-200 rounded bg-white overflow-hidden p-4 lg:p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold">Select Device to Replace</h3>
                        <button type="button" onClick={() => setIsManualEntry(true)} className="text-sm text-blue-600 hover:underline">Device not listed?</button>
                      </div>
                      
                      {selectedUserEquipment.length === 0 ? (
                        <p className="text-gray-500 italic text-sm">No equipment found for this user.</p>
                      ) : (
                        <div className="space-y-3">
                          {selectedUserEquipment.map((item) => (
                            <label key={item.serial || item.sku} className="flex items-start gap-2 lg:gap-3 p-2 lg:p-3 border rounded hover:bg-gray-50 cursor-pointer">
                              <input 
                                type="radio" 
                                className="mt-1"
                                value={item.serial || item.sku}
                                {...methods.register('selectedDeviceId')}
                              />
                              <div>
                                <p className="font-medium">{item.type} - {item.sku || 'Unknown Model'}</p>
                                {item.serial && <p className="text-sm text-gray-500">SN: {item.serial}</p>}
                              </div>
                            </label>
                          ))}
                          {methods.formState.errors.selectedDeviceId && (
                            <p className="text-red-500 text-sm">{methods.formState.errors.selectedDeviceId.message as string}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded bg-white overflow-hidden p-4 lg:p-6 space-y-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-bold">Select Device to Replace</h3>
                        {formValues.fullName && (
                          <button type="button" onClick={() => { setIsManualEntry(false); methods.setValue('manualDeviceType', ''); }} className="text-sm text-blue-600 hover:underline">Select from list</button>
                        )}
                      </div>
                      <div className="space-y-3">
                        <p className="font-semibold text-sm text-gray-700">Device Type</p>
                        <div className="grid grid-cols-2 gap-3">
                          {(categories || [])
                            .filter(c => !c.hideFromGenerics)
                            .filter(c => REQUIRE_SERIALIZED_ONLY ? c.isSerialized === true : true)
                            .map(c => (
                              <label key={c.name} className="flex items-center gap-2 lg:gap-3 p-2 lg:p-3 border rounded hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="radio"
                                  value={c.name}
                                  {...methods.register('manualDeviceType')}
                                />
                                <span className="font-medium">{c.name}</span>
                              </label>
                          ))}
                        </div>
                        {methods.formState.errors.manualDeviceType && (
                          <p className="text-red-500 text-sm">{methods.formState.errors.manualDeviceType.message as string}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {(formValues.selectedDeviceId || formValues.manualDeviceType) && (
                    <div className="border border-gray-200 rounded bg-white overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                        <h5 className="text-sm font-semibold text-gray-500 uppercase">Device Details</h5>
                      </div>
                      <div className="p-0">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="font-bold text-left px-3 py-2 text-gray-700">Make / Model <span className="text-red-500">*</span></th>
                              <th className="font-bold text-left px-3 py-2 text-gray-700">Serial Number <span className="text-red-500">*</span></th>
                              <th className="font-bold text-left px-3 py-2 text-gray-700">Asset Tag</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            <tr>
                              <td className="p-1 border-r border-gray-200">
                                <Controller
                                  name="faultyMakeModel"
                                  control={methods.control}
                                  render={({ field, fieldState }) => (
                                    <input
                                      {...field}
                                      className={`w-full bg-transparent border-0 focus:ring-0 px-2 py-1 ${fieldState.error ? 'text-red-900 placeholder-red-300 bg-red-50' : (!field.value ? 'placeholder-red-300 bg-red-50' : '')}`}
                                      placeholder="Make/Model"
                                    />
                                  )}
                                />
                              </td>
                              <td className="p-1 border-r border-gray-200">
                                <Controller
                                  name="faultySerialNumber"
                                  control={methods.control}
                                  render={({ field, fieldState }) => (
                                    <input
                                      {...field}
                                      className={`w-full bg-transparent border-0 focus:ring-0 px-2 py-1 ${fieldState.error ? 'text-red-900 placeholder-red-300 bg-red-50' : (!field.value ? 'placeholder-red-300 bg-red-50' : '')}`}
                                      placeholder="Serial Number"
                                    />
                                  )}
                                />
                              </td>
                              <td className="p-1">
                                <Controller
                                  name="faultyAssetTag"
                                  control={methods.control}
                                  render={({ field }) => (
                                    <input
                                      {...field}
                                      className="w-full bg-transparent border-0 focus:ring-0 px-2 py-1 placeholder:italic placeholder:text-gray-400"
                                      placeholder="Optional"
                                    />
                                  )}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="border border-gray-200 rounded bg-white p-4 lg:p-6 space-y-4">
                    <h3 className="text-lg font-bold mb-2">Replacement Reason</h3>
                    <TextAreaField id="replacementReason" label="Reason for Replacement (e.g. Screen broken, won't turn on)" />
                  </div>
                </>
              )}

              {currentStep === 2 && (
                <>
                  {(formValues.selectedDeviceId || formValues.manualDeviceType) && (
                    <div className="border border-gray-200 rounded bg-white p-4 lg:p-6 space-y-4">
                      <h3 className="text-lg font-bold mb-2">Select Replacement Device</h3>
                      <div className="space-y-3">
                        {availableReplacements.length === 0 && (
                          <p className="text-gray-500 italic text-sm">No buffer stock available for this category. Please use a manual override.</p>
                        )}
                        {availableReplacements.map(item => (
                          <label key={item.sku} className="flex items-start gap-2 lg:gap-3 p-2 lg:p-3 border rounded hover:bg-gray-50 cursor-pointer">
                            <input
                              type="radio"
                              className="mt-1"
                              value={item.sku}
                              {...methods.register('replacementSelection')}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900">{item.item}</span>
                              <span className="text-xs text-gray-500 mt-0.5">SKU: {item.sku} | Loc: {item.stockBreakdown ? item.stockBreakdown.map((b: any) => `${b.site} (${b.qty})`).join(' | ') : (item.stockLocation || 'N/A')}</span>
                              <span className="text-sm font-semibold text-blue-600 mt-1">Available: {item.stockQuantity}</span>
                            </div>
                          </label>
                        ))}
                        <label className="flex items-start gap-2 lg:gap-3 p-2 lg:p-3 border rounded hover:bg-gray-50 cursor-pointer bg-orange-50/50 border-orange-200">
                          <input
                            type="radio"
                            className="mt-1"
                            value="OVERRIDE"
                            {...methods.register('replacementSelection')}
                          />
                          <div className="flex flex-col w-full">
                            <span className="font-medium text-orange-800">Manual Override / Backorder</span>
                            <span className="text-xs text-orange-600 mt-0.5">In the event that there is no suitable device listed above, please select this option.</span>
                            {formValues.replacementSelection === 'OVERRIDE' && (
                              <div className="mt-3 w-full">
                                <TextAreaField 
                                  id="replacementOverrideText" 
                                  label="Provide details of how you would like us to handle the replacement of this device: " 
                                />
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                      {methods.formState.errors.replacementSelection && (
                        <p className="text-red-500 text-sm">{methods.formState.errors.replacementSelection.message as string}</p>
                      )}
                    </div>
                  )}
                  {formValues.replacementSelection === 'OVERRIDE' && (
                    <div className="border border-gray-200 rounded bg-white p-4 lg:p-6 space-y-4 mt-4">
                      <h3 className="text-lg font-bold mb-2">Add-On: Loaner Device</h3>
                      <div className="space-y-3">
                        {loanerCards.length === 0 && (
                          <p className="text-gray-500 italic text-sm">No loaner options available.</p>
                        )}
                        {loanerCards.map(item => {
                          const descriptionText = item.description?.value?.[0]?.children?.[0]?.text as string | undefined;
                          return (
                            <label key={item.id} className="flex items-start gap-2 lg:gap-3 p-2 lg:p-3 border rounded hover:bg-gray-50 cursor-pointer">
                              <input
                                type="radio"
                                className="mt-1"
                                value={item.id}
                                {...methods.register('selectedLoanerId')}
                              />
                              <div className="flex flex-col w-full">
                                <div className="flex justify-between items-center w-full">
                                  <span className="font-medium text-gray-900">{item.name}</span>
                                  <span className="font-medium text-gray-900">
                                    {formatCurrency(((item.price?.price || 0) / 100), company?.region?.code)}
                                  </span>
                                </div>
                                {descriptionText && (
                                  <span className="text-sm text-gray-500 mt-1 block">
                                    {descriptionText}
                                  </span>
                                )}
                              </div>
                            </label>
                          );
                        })}
                        {loanerCards.length > 0 && (
                          <label className="flex items-start gap-2 lg:gap-3 p-2 lg:p-3 border rounded hover:bg-gray-50 cursor-pointer bg-gray-50/50">
                            <input
                              type="radio"
                              className="mt-1"
                              value=""
                              {...methods.register('selectedLoanerId')}
                            />
                            <span className="font-medium text-gray-700">None</span>
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {currentStep === 3 && (
                <>
                  <div className="border border-gray-200 rounded bg-white p-4 lg:p-6 space-y-4">
                    <h3 className="text-lg font-bold mb-2">Return Device Processing</h3>
                    <div className="space-y-3">
                      {returnProcessingCards.length === 0 && (
                        <p className="text-gray-500 italic text-sm">No return processing options available.</p>
                      )}
                      {returnProcessingCards.map(item => {
                        const descriptionText = item.description?.value?.[0]?.children?.[0]?.text as string | undefined;
                        return (
                          <label key={item.id} className="flex items-start gap-2 lg:gap-3 p-2 lg:p-3 border rounded hover:bg-gray-50 cursor-pointer">
                            <input
                              type="radio"
                              className="mt-1"
                              value={item.id}
                              {...methods.register('selectedReturnProcessingId')}
                            />
                            <div className="flex flex-col w-full">
                              <div className="flex justify-between items-center w-full">
                                <span className="font-medium text-gray-900">{item.name}</span>
                                <span className="font-medium text-gray-900">
                                  {formatCurrency(((item.price?.price || 0) / 100), company?.region?.code)}
                                </span>
                              </div>
                              {descriptionText && (
                                <span className="text-sm text-gray-500 mt-1 block">
                                  {descriptionText}
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })}

                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-200">
                <div className="flex gap-4">
                  {currentStep > 1 && (
                    <Button variant={ButtonVariant.Secondary} onClick={handlePrevStep} type="button">
                      Back
                    </Button>
                  )}
                  <Button
                    variant={ButtonVariant.Secondary}
                    onClick={resetForm}
                    type="button"
                  >
                    Clear Form
                  </Button>
                </div>
                
                {currentStep < 3 && (
                  <Button variant={ButtonVariant.Primary} onClick={handleNextStep} type="button">
                    Next Step
                  </Button>
                )}
                
                {currentStep === 3 && (
                  <Button variant={ButtonVariant.Primary} type="submit">
                    Continue
                  </Button>
                )}
              </div>
            </div>
          }
          rightColumn={
            <div className="space-y-4">
              <div className="border p-4 rounded bg-gray-50 shadow-sm space-y-4">
                <div className="flex justify-between items-end border-b pb-2">
                  <h4 className="text-xl font-bold">Basket</h4>
                  {selectedUserEquipment?.find((e: any) => (e.serial || e.sku) === formValues.selectedDeviceId)?.hasAdrWarranty && (
                    <span className="text-primary-700 text-sm font-semibold text-right">
                      Existing ADR Warranty Found
                    </span>
                  )}
                </div>
                
                {(() => {
                  if (inferredServices.length === 0) {
                    return <p className="text-sm text-gray-500 italic">No hardware selected</p>;
                  }
                  
                  const adminFees = inferredServices.filter(s => s.listSection === 'admin');
                  const logisticsItems = inferredServices.filter(s => s.listSection === 'logistics');
                  const processingItems = inferredServices.filter(s => s.isInventoryItem === true || s.listSection === 'processing');
                  const addonsItems = inferredServices.filter(s => s.listSection === 'addons');

                  const renderService = (service: any, idx: string | number) => {
                    if (service.isInventoryItem) {
                      return (
                        <div key={idx} className="p-3 bg-white border rounded">
                          <div className="flex justify-between items-start text-sm">
                            <div className="pr-4">
                              <span className="font-medium">{service.quantity}x</span> {service.name}
                            </div>
                            <div className="font-medium whitespace-nowrap italic text-gray-500">
                              
                            </div>
                          </div>
                          {service.hasLifecyclePack && (
                            <span className="italic text-green-600 text-xs block mt-1">
                              Lifecycle Pack Detected<br />Costs will be adjusted accordingly
                            </span>
                          )}
                        </div>
                      );
                    }

                    const associatedPhysicalItems = (service as any).associatedPhysicalItems as Array<{ type: string, hasLifecyclePack: boolean, isCollectionItem?: boolean, isManualEntry?: boolean, suppressSerialInput?: boolean }>;
                    const physicalTypeGroups: Record<string, { count: number, type: string, isCollectionItem: boolean, isManualEntry: boolean, suppressSerialInput: boolean }> = {};
                    
                    if (associatedPhysicalItems) {
                      associatedPhysicalItems.forEach(item => {
                        if (!physicalTypeGroups[item.type]) physicalTypeGroups[item.type] = { count: 0, type: item.type, isCollectionItem: !!item.isCollectionItem, isManualEntry: !!item.isManualEntry, suppressSerialInput: !!item.suppressSerialInput };
                        physicalTypeGroups[item.type].count++;
                      });
                    }

                    return (
                      <div key={idx} className="p-3 bg-white border rounded">
                        <div className="flex justify-between items-start text-sm">
                          <div className="pr-4">
                            <span className="font-medium">{service.quantity}x</span> {service.name || service.rateCard?.name}
                          </div>
                          <div className="font-medium whitespace-nowrap">
                            {service.originalPrice !== undefined && (
                              <span className="line-through text-primary-700 mr-2">
                                {formatCurrency(service.originalPrice, company?.region?.code)}
                              </span>
                            )}
                            {formatCurrency(service.total ?? (((service.rateCard?.price?.price || 0) / 100) * service.quantity), company?.region?.code)}
                          </div>
                        </div>
                        
                        {associatedPhysicalItems && Object.keys(physicalTypeGroups).length > 0 && (
                          <div className="mt-2 space-y-1 pt-2 border-t border-gray-100 pl-4 border-l-2 border-l-blue-200">
                            {Object.values(physicalTypeGroups).map((group, groupIdx) => (
                              <p key={groupIdx} className="text-sm font-medium text-gray-700">{group.count}x {group.type}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  };

                  return (
                    <div className="space-y-4">
                      {adminFees.length > 0 && (
                        <div className="space-y-3">
                          <h5 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase">
                            Administration
                            <InfoTooltip id="admin-info" text={getAdminTooltipText(company?.name ?? 'Company')} place="left-start" className="!max-w-none" />
                          </h5>
                          {adminFees.map((service, idx) => renderService(service, `admin-${idx}`))}
                        </div>
                      )}
                      {logisticsItems.length > 0 && (
                        <div className="space-y-3 pt-4 border-t border-gray-200">
                          <h5 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase">
                            Logistics & Insurance
                            <InfoTooltip id="logistics-info" text={logisticsTooltipText} place="left" className="!max-w-none" />
                          </h5>
                          {logisticsItems.map((service, idx) => renderService(service, `logistics-${idx}`))}
                        </div>
                      )}
                      {processingItems.length > 0 && (
                        <div className="space-y-3 pt-4 border-t border-gray-200">
                          <h5 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase">
                            Processing
                            <InfoTooltip id="processing-info" text={processingTooltipText} place="left" className="!max-w-none" />
                          </h5>
                          {processingItems.map((service, idx) => renderService(service, `proc-${idx}`))}
                        </div>
                      )}
                      {addonsItems.length > 0 && (
                        <div className="space-y-3 pt-4 border-t border-gray-200">
                          <h5 className="text-sm font-semibold text-gray-500 uppercase">ADD-ONS</h5>
                          {addonsItems.map((service, idx) => renderService(service, `addons-${idx}`))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="flex justify-between items-center pt-4 text-lg font-bold border-t border-gray-300 mt-4">
                  <span>Total Estimate</span>
                  <span>{formatCurrency(runningTotal, company?.region?.code)}</span>
                </div>
              </div>
            </div>
          }
        />
      </Form>

      {checkoutPayload && (
        <AdvDeviceReplacementModal
          {...modalProps}
          mspId={partnerId}
          customerId={companyId}
          checkoutPayload={checkoutPayload}
          uniqueUsers={uniqueUsers}
          onSuccessCallback={() => {
            resetForm();
            toast.success('Service Request Submitted Successfully');
            setTimeout(() => {
              navigate(`/msps/${partnerId}/companies/${companyId}/management/submittedRequests`);
            }, 0);
          }}
        />
      )}
    </>
  );
};
